/**
 * Login com Apple no app nativo.
 *
 * POR QUE EXISTE: a diretriz 4.8 da App Review exige que um app que oferece
 * login de terceiro ofereca tambem uma alternativa equivalente que preserve
 * privacidade. Nao substitui o Google — convive com ele.
 *
 * SO NO iOS. No Android e na web o botao nao aparece: a Apple nao exige nada
 * fora da plataforma dela, e um terceiro metodo de entrada onde ninguem pediu
 * so aumenta a chance de a confeiteira esquecer por onde entrou.
 *
 * O NOME SO VEM UMA VEZ. A Apple entrega `givenName`/`familyName` apenas na
 * PRIMEIRA autorizacao, e nunca mais — nem se o app for reinstalado. Por isso
 * mandamos para o backend junto com o token, para ele gravar naquele instante.
 */

import { isNative, getPlatform, getPlugin } from './native';
import { captureMessage } from '../pwa/sentry';

let iniciado = false;

function plugin() {
  return getPlugin('SocialLogin');
}

export function disponivel() {
  return isNative() && getPlatform() === 'ios' && !!plugin();
}

/** Guarda o motivo tecnico da ultima falha, para a tela poder mostrar. */
let ultimoErro = null;

async function garantirInicializado(p) {
  if (iniciado) return true;
  try {
    // redirectUrl VAZIO e obrigatorio no iOS — a documentacao do plugin diz
    // "use empty string for iOS to prevent redirect". Passar o objeto sem a
    // chave fazia o initialize falhar. O clientId nao entra: no iOS nativo a
    // audiencia e o proprio bundle ID.
    await p.initialize({ apple: { redirectUrl: '' } });
    iniciado = true;
    return true;
  } catch (e) {
    ultimoErro = 'init: ' + ((e && (e.message || e.code)) || 'desconhecido');
    // Nao ha como ler log do iOS sem um Mac. O Sentry e o unico canal que
    // traz o motivo real de um aparelho que nao esta na minha mesa.
    captureMessage('apple-login-init-falhou', 'error', { detalhe: ultimoErro });
    return false;
  }
}

/** Motivo tecnico da ultima falha. So para diagnostico — nao mostrar sozinho. */
export function detalheDoErro() {
  return ultimoErro;
}

/**
 * Abre o fluxo nativo e devolve o token para trocar por sessao no backend.
 *
 * @returns {Promise<{ ok: boolean, idToken?: string, nome?: string|null, motivo?: string }>}
 *   motivo: 'indisponivel' | 'cancelado' | 'sem-token' | 'erro'
 *
 * Cancelar nao e erro: a pessoa fechou a folha de propósito.
 */
export async function entrar() {
  const p = plugin();
  if (!p) return { ok: false, motivo: 'indisponivel' };
  if (!(await garantirInicializado(p))) return { ok: false, motivo: 'erro' };

  try {
    const r = await p.login({ provider: 'apple', options: {} });
    const res = r?.result || r || {};

    // O formato varia entre versoes do plugin; procuramos onde o token costuma
    // vir, em vez de assumir um caminho so.
    const idToken = res.idToken || res.identityToken || r?.idToken || null;
    if (!idToken) return { ok: false, motivo: 'sem-token' };

    const perfil = res.profile || res.user || {};
    const nome = [perfil.givenName, perfil.familyName]
      .filter(Boolean).join(' ').trim() || null;

    return { ok: true, idToken, nome };
  } catch (e) {
    const bruto = ((e && (e.message || e.code)) || '').toString();
    const msg = bruto.toLowerCase();
    if (msg.includes('cancel') || msg.includes('1001')) return { ok: false, motivo: 'cancelado' };
    ultimoErro = 'login: ' + (bruto || 'desconhecido');
    captureMessage('apple-login-falhou', 'error', { detalhe: ultimoErro });
    return { ok: false, motivo: 'erro' };
  }
}

/** Encerra a sessao da Apple no aparelho (usado no logout). */
export async function sair() {
  const p = plugin();
  if (!p || !iniciado) return;
  try {
    await p.logout({ provider: 'apple' });
  } catch { /* melhor esforco: o logout do app nao depende disto */ }
}
